import { useMemo, useRef, useLayoutEffect } from 'react';
import { useT } from '@/i18n/LanguageContext';
import { useHolidays } from '@/hooks/useHolidays';
import { dispText } from '../plannerConstants';

const DAY = 30; // px per day

const PRIO_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };

const toDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
  return isNaN(d) ? null : d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const diffDays = (a, b) => Math.round((a - b) / 86400000);

// Lightweight Gantt. items: [{ id, label, sub, start, end, color, priority? }]. onItemClick(item).
// Rows with a `priority` are ordered highest-first; the view auto-scrolls to today on load.
// rowAction(item) — optional render prop for a per-row action shown in the label column.
export default function GanttChart({ items = [], onItemClick, emptyText, rowAction, maxHeightClass = 'max-h-[72vh]' }) {
  const { t } = useT();
  const scrollRef = useRef(null);
  // 平日・祝日 対応 — dominant year comes from mid-point of the visible range.
  // Use a rough estimate based on the first item's start; hook auto-refreshes
  // if empty. This means most tasks land in one year, which is the common case.
  const yearHint = useMemo(() => {
    if (!items.length) return new Date().getFullYear();
    const s = items[0].start || items[0].end;
    return s ? parseInt(String(s).slice(0, 4), 10) : new Date().getFullYear();
  }, [items]);
  const { holidayName, isWeekend, isHoliday } = useHolidays(yearHint);

  const rows = useMemo(() => {
    const mapped = items.map((it, i) => {
      let s = toDate(it.start), e = toDate(it.end);
      if (!s && e) s = e;
      if (!e && s) e = s;
      return { ...it, s, e, _i: i };
    }).filter((r) => r.s && r.e);
    // 日付が無い・読めない行は棒を描けない。黙って消すと「無いこと」に気付けない。
    mapped.undated = items.filter((it) => !toDate(it.start) && !toDate(it.end));
    // 優先度 → 終了日の早い順（同日なら開始日 → 元の並び）で自動整列
    return mapped.sort((a, b) => {
      const pa = PRIO_RANK[a.priority] ?? 99;
      const pb = PRIO_RANK[b.priority] ?? 99;
      return (pa - pb) || (a.e - b.e) || (a.s - b.s) || (a._i - b._i);
    });
  }, [items]);

  // auto-scroll the timeline so today's line is centered on load / data change
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || rows.length === 0) return;
    const allDates = rows.flatMap((r) => [r.s, r.e]);
    const min = addDays(new Date(Math.min(...allDates)), -2);
    const max = addDays(new Date(Math.max(...allDates)), 2);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (today < min || today > max) return;
    const todayLeft = 200 + diffDays(today, min) * DAY + DAY / 2;
    el.scrollLeft = Math.max(0, todayLeft - el.clientWidth / 2);
  }, [rows]);

  const undated = rows.undated || [];

  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-zinc-400" data-testid="planner-gantt-empty">
        {emptyText || t('planner.gantt.empty')}
        {undated.length > 0 && (
          <p className="mt-2 text-xs text-amber-700" data-testid="planner-gantt-undated-empty">
            {t('planner.gantt.undatedEmpty', { n: undated.length })}
          </p>
        )}
      </div>
    );
  }

  const allDates = rows.flatMap((r) => [r.s, r.e]);
  let min = new Date(Math.min(...allDates));
  let max = new Date(Math.max(...allDates));
  min = addDays(min, -2);
  max = addDays(max, 2);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const totalDays = diffDays(max, min) + 1;
  const width = totalDays * DAY;

  const days = Array.from({ length: totalDays }, (_, i) => addDays(min, i));
  const months = [];
  days.forEach((d, i) => {
    const key = `${d.getFullYear()}/${d.getMonth() + 1}`;
    const last = months[months.length - 1];
    if (!last || last.key !== key) months.push({ key, start: i, count: 1 });
    else last.count++;
  });

  return (
    <div ref={scrollRef}
      className={`overflow-auto rounded-xl border border-zinc-200 bg-white ${maxHeightClass}`}
      data-testid="planner-gantt">
      <div className="relative" style={{ minWidth: 200 + width }}>
        {today >= min && today <= max && (
          <>
            <div className="absolute top-0 bottom-0 w-px bg-rose-500 z-[25] pointer-events-none"
              style={{ left: 200 + diffDays(today, min) * DAY + DAY / 2 }} data-testid="planner-gantt-today-line" />
            <div className="absolute top-0 -translate-x-1/2 z-[26] pointer-events-none rounded-b bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 shadow"
              style={{ left: 200 + diffDays(today, min) * DAY + DAY / 2 }} data-testid="planner-gantt-today-label">{t('planner.gantt.today')}</div>
          </>
        )}
        <div className="flex sticky top-0 z-20 bg-zinc-50 border-b border-zinc-200">
          <div className="w-[200px] shrink-0 sticky left-0 z-30 bg-zinc-50 border-r border-zinc-200 px-3 py-2 text-xs font-semibold text-zinc-500">{t('planner.gantt.item')}</div>
          <div className="relative" style={{ width }}>
            <div className="flex">
              {months.map((m) => (
                <div key={m.key} style={{ width: m.count * DAY }} className="border-r border-zinc-200 px-2 py-1 text-[11px] font-semibold text-zinc-600">{m.key}</div>
              ))}
            </div>
            <div className="flex border-t border-zinc-200">
              {days.map((d) => {
                const hName = holidayName(d);
                const wknd = isWeekend(d);
                const nonWork = wknd || !!hName;
                return (
                  <div key={d.getTime()}
                    title={hName || (wknd ? t(d.getDay() === 0 ? 'planner.gantt.sunday' : 'planner.gantt.saturday') : undefined)}
                    style={{ width: DAY }}
                    className={`text-center text-[10px] py-0.5 ${
                      hName ? 'bg-rose-100/70 text-rose-600 font-semibold' :
                      wknd ? (d.getDay() === 0 ? 'bg-rose-50/60 text-rose-500' : 'bg-sky-50/60 text-sky-600') :
                      'text-zinc-400'
                    }`}
                    data-nonworking={nonWork ? '1' : undefined}
                  >
                    {d.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row background stripes for weekends + holidays — sits behind the task bars */}
        <div className="pointer-events-none absolute z-[1]" style={{ left: 200, top: 44, right: 0, bottom: 0 }}>
          <div className="relative h-full" style={{ width }}>
            {days.map((d, i) => {
              if (!(isWeekend(d) || isHoliday(d))) return null;
              const hName = holidayName(d);
              return (
                <div key={d.getTime()}
                  className={`absolute top-0 bottom-0 ${
                    hName ? 'bg-rose-100/40' : (d.getDay() === 0 ? 'bg-rose-50/50' : 'bg-sky-50/50')
                  }`}
                  style={{ left: i * DAY, width: DAY }} />
              );
            })}
          </div>
        </div>

        {undated.length > 0 && (
          <div className="sticky left-0 z-10 border-b border-amber-100 bg-amber-50/70 px-3 py-1.5 text-[11px] text-amber-800"
            data-testid="planner-gantt-undated">
            {t('planner.gantt.undatedRow', {
              n: undated.length,
              names: `${undated.slice(0, 3).map((u) => u.label).join('、')}${undated.length > 3 ? ' …' : ''}`,
            })}
          </div>
        )}
        {rows.map((r) => {
          const left = diffDays(r.s, min) * DAY;
          const w = (diffDays(r.e, r.s) + 1) * DAY;
          const rowH = r.sub ? 52 : 42;
          return (
            <div key={r.id} className="flex border-b border-zinc-50 hover:bg-zinc-50/50">
              <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-white border-r border-zinc-200 px-3 py-2 flex items-center gap-1">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-800 truncate" title={r.label}><span data-no-translate>{dispText(r.label)}</span></p>
                  <p className="text-[11px] text-zinc-400 truncate flex items-center gap-1">
                    {r.sub}
                    {/* 期限超過 is a SEPARATE fact from finished. */}
                    {r.overdue && (
                      <span className="text-rose-600 font-medium" data-testid={`gantt-overdue-${r.id}`}>
                        {t('planner.stat.overdue')}
                      </span>
                    )}
                  </p>
                </div>
                {rowAction && <div className="shrink-0">{rowAction(r)}</div>}
              </div>
              <div className="relative" style={{ width, height: rowH }}>
                <button type="button" onClick={() => onItemClick?.(r)} title={r.label} data-testid={`gantt-bar-${r.id}`}
                  className="absolute top-1/2 -translate-y-1/2 h-6 rounded-md shadow-sm text-[11px] text-white px-2 flex items-center hover:brightness-110 transition-all z-[5]"
                  style={{ left, width: Math.max(w, DAY), backgroundColor: r.color || '#0F766E' }}>
                  {/* 棒が短いと名前が切れて読めない。狭いときは棒の外（右）に出す。 */}
                  {Math.max(w, DAY) >= 72
                    ? <span className="truncate"><span data-no-translate>{dispText(r.label)}</span></span>
                    : (
                      <span className="absolute left-[calc(100%+6px)] whitespace-nowrap text-[11px] text-zinc-600"
                        data-testid={`gantt-bar-label-${r.id}`}><span data-no-translate>{dispText(r.label)}</span></span>
                    )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
